import React, { useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { Button, Alert } from "react-bootstrap";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import BriqueDevisComponent from "./BriqueDevisComponent";
import BriqueFloatingNav from "./BriqueFloatingNav";
import {
  normalizeLayoutSegments,
  patchItemWithLayoutSegments,
} from "helper/briqueLayoutSegments";
import {
  BRIQUES_ORDER_TYPE,
  briquesOrderDroppableId,
  briqueCardDraggableId,
} from "helper/briqueDragDropIds";

/**
 * BriquesSection
 *
 * Section d'édition de briques pour le devis.
 * Gère la liste des briques ajoutées au devis, permet d'en ajouter
 * depuis les templates ou d'en créer de zéro.
 *
 * Ce composant est intégré dans MultiOrders.js en complément (et non
 * en remplacement) du système actuel PR/CKT/PDJ.
 *
 * Props :
 *   briquesDevis   {array}    Briques actuellement dans le devis
 *   onChange       {function} Callback appelée avec le nouveau tableau de briques
 *   formatTagId    {number}   ID du format de prestation (filtrage catalogue dans chaque brique si « lié au format »)
 *   onRemoveBrique {function} Optionnel : (devisId, brique) après suppression (ex. mémoriser un défaut retiré)
 *   orderDndKey     {string}  Clé stable pour le DnD (ex. index de prestation)
 */
const BriquesSection = ({
  briquesDevis = [],
  onChange,
  formatTagId = null,
  onRemoveBrique = null,
  orderDndKey = "0",
}) => {
  const briquesState   = useSelector((s) => s.adminData.briques);
  const briquesTemplates = briquesState?.data || [];
  const [showSelector, setShowSelector] = useState(false);

  // Tous les templates actifs sont proposables ; le filtre par format se fait dans chaque brique (booléen API).
  const filteredTemplates = briquesTemplates;

  const briquesListDroppableId = briquesOrderDroppableId(orderDndKey);

  const handleMasterDragEnd = useCallback(
    (result) => {
      if (!result.destination) return;
      const { source, destination } = result;
      const destId = destination.droppableId;
      const srcId = source.droppableId;

      if (destId === briquesListDroppableId && srcId === briquesListDroppableId) {
        if (source.index === destination.index) return;
        const next = Array.from(briquesDevis);
        const [removed] = next.splice(source.index, 1);
        next.splice(destination.index, 0, removed);
        onChange(next.map((b, i) => ({ ...b, sort: i })));
        return;
      }

      const parseSeg = (prefix) => {
        if (!destId.startsWith(prefix) || srcId !== destId) return null;
        const rest = destId.slice(prefix.length);
        if (rest.includes("__")) {
          const i = rest.indexOf("__");
          return { devisId: rest.slice(0, i), itemLocalId: rest.slice(i + 2) };
        }
        const itemLocalId = rest;
        for (const b of briquesDevis) {
          const found = (b.items || []).some((it) => it._localId === itemLocalId);
          if (found) {
            return { devisId: b._devisId, itemLocalId };
          }
        }
        return null;
      };

      const applySegmentReorder = (prefix, matcher) => {
        const parsed = parseSeg(prefix);
        if (!parsed) return;
        const { devisId, itemLocalId } = parsed;
        if (source.index === destination.index) return;
        onChange(
          briquesDevis.map((b) => {
            if (b._devisId !== devisId) return b;
            const items = (b.items || []).map((it) => {
              if (!matcher(it, itemLocalId)) return it;
              const segs = normalizeLayoutSegments(it);
              const next = Array.from(segs);
              const [removed] = next.splice(source.index, 1);
              next.splice(destination.index, 0, removed);
              return patchItemWithLayoutSegments(it, next);
            });
            return { ...b, items };
          })
        );
      };

      if (destId.startsWith("forfait-segments")) {
        applySegmentReorder("forfait-segments__", (it, lid) => it._localId === lid && it.type === "forfait");
        if (destId.startsWith("forfait-segments__")) return;
        applySegmentReorder("forfait-segments-", (it, lid) => it._localId === lid && it.type === "forfait");
        return;
      }
      if (destId.startsWith("group-segments")) {
        applySegmentReorder("group-segments__", (it, lid) => it._localId === lid && it.type === "group");
        if (destId.startsWith("group-segments__")) return;
        applySegmentReorder("group-segments-", (it, lid) => it._localId === lid && it.type === "group");
        return;
      }
      if (destId.startsWith("article-segments")) {
        applySegmentReorder(
          "article-segments__",
          (it, lid) => it._localId === lid && (it.type === "product" || it.type === "part")
        );
        if (destId.startsWith("article-segments__")) return;
        applySegmentReorder(
          "article-segments-",
          (it, lid) => it._localId === lid && (it.type === "product" || it.type === "part")
        );
        return;
      }

      const itemsPrefix = "brique-items-";
      if (destId.startsWith(itemsPrefix) && srcId === destId) {
        const devisKey = destId.slice(itemsPrefix.length);
        const target = briquesDevis.find(
          (b) => String(b._devisId || b.id || "new") === devisKey
        );
        if (!target || source.index === destination.index) return;
        const arr = Array.from(target.items || []);
        const [removed] = arr.splice(source.index, 1);
        arr.splice(destination.index, 0, removed);
        onChange(
          briquesDevis.map((b) =>
            b._devisId === target._devisId
              ? { ...b, items: arr.map((it, i) => ({ ...it, sort: i })) }
              : b
          )
        );
      }
    },
    [briquesDevis, briquesListDroppableId, onChange]
  );

  // -------------------------------------------------------------------------
  // Ajout d'une brique depuis un template
  // Une brique n'a pas d'items pré-définis : l'utilisateur les choisit
  // dans le picker filtré par les tags de la brique.
  // -------------------------------------------------------------------------
  const addBriqueFromTemplate = (template) => {
    const newBrique = {
      ...template,
      _devisId: `devis_brique_${Date.now()}`,
      items:    [],
    };
    onChange([...briquesDevis, newBrique]);
    setShowSelector(false);
  };

  // -------------------------------------------------------------------------
  // Ajout d'une brique vide (sans template)
  // -------------------------------------------------------------------------
  const addEmptyBrique = () => {
    const newBrique = {
      _devisId: `devis_brique_${Date.now()}`,
      id:       null,
      name:     "Nouvelle brique",
      items:    [],
      linked_to_prestation_format: false,
    };
    onChange([...briquesDevis, newBrique]);
  };

  // -------------------------------------------------------------------------
  // Mise à jour d'une brique
  // -------------------------------------------------------------------------
  const updateBrique = useCallback((devisId, updatedBrique) => {
    onChange(briquesDevis.map((b) => (b._devisId === devisId ? updatedBrique : b)));
  }, [briquesDevis, onChange]);

  // -------------------------------------------------------------------------
  // Suppression d'une brique
  // -------------------------------------------------------------------------
  const removeBrique = (devisId) => {
    const removed = briquesDevis.find((b) => b._devisId === devisId);
    if (onRemoveBrique) {
      onRemoveBrique(devisId, removed);
    }
    onChange(briquesDevis.filter((b) => b._devisId !== devisId));
  };

  // -------------------------------------------------------------------------
  // Rendu
  // -------------------------------------------------------------------------
  return (
    <>
      {/* Briques dans le devis — un seul DragDropContext pour briques + contenu */}
      {briquesDevis.length > 0 && (
        <DragDropContext onDragEnd={handleMasterDragEnd}>
          <Droppable droppableId={briquesListDroppableId} type={BRIQUES_ORDER_TYPE}>
            {(listProvided) => (
              <div ref={listProvided.innerRef} {...listProvided.droppableProps}>
                {briquesDevis.map((brique, bIndex) => (
                  <Draggable
                    key={brique._devisId}
                    draggableId={briqueCardDraggableId(brique._devisId)}
                    index={bIndex}
                    type={BRIQUES_ORDER_TYPE}
                  >
                    {(cardDrag) => {
                      const { style: dragStyle, ...dragRest } = cardDrag.draggableProps;
                      return (
                      <div
                        ref={cardDrag.innerRef}
                        {...dragRest}
                        id={`brique-anchor-${brique._devisId}`}
                        className="mb-1"
                        style={{
                          ...(dragStyle || {}),
                          scrollMarginTop: "20px",
                        }}
                      >
                        <BriqueDevisComponent
                          brique={brique}
                          onUpdate={(updated) => updateBrique(brique._devisId, updated)}
                          onRemove={() => removeBrique(brique._devisId)}
                          formatTagId={formatTagId}
                          outerDragHandleProps={cardDrag.dragHandleProps}
                        />
                      </div>
                      );
                    }}
                  </Draggable>
                ))}
                {listProvided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}

      <BriqueFloatingNav briquesDevis={briquesDevis} />

      {/* Sélecteur de templates */}
      {showSelector && (
        <div className="brique-selector border rounded p-3 mb-3 bg-light">
          <h6 className="mb-2">Choisir un template de brique</h6>
          {filteredTemplates.length === 0 ? (
            <Alert variant="info" className="mb-0 small">
              Aucun template de brique actif.
            </Alert>
          ) : (
            <div className="d-flex flex-wrap" style={{ gap: "0.5rem" }}>
              {filteredTemplates.map((template) => (
                <Button
                  key={template.id}
                  variant="outline-primary"
                  size="sm"
                  onClick={() => addBriqueFromTemplate(template)}
                >
                  {template.name}
                  {template.item_count > 0 && (
                    <span className="ml-1 badge badge-light">{template.item_count}</span>
                  )}
                </Button>
              ))}
            </div>
          )}
          <div className="mt-2">
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={() => setShowSelector(false)}
            >
              Annuler
            </Button>
          </div>
        </div>
      )}

      {/* Boutons d'ajout */}
      {!showSelector && (
        <div className="d-flex flex-wrap" style={{ gap: '0.5rem' }}>
          {filteredTemplates.length > 0 && (
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => setShowSelector(true)}
            >
              <FontAwesomeIcon icon={faPlus} className="mr-1" />
              Ajouter une brique (template)
            </Button>
          )}
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={addEmptyBrique}
          >
            <FontAwesomeIcon icon={faPlus} className="mr-1" />
            Brique vide
          </Button>
        </div>
      )}
    </>
  );
};

export default BriquesSection;
